import React, {
  useEffect, useState, useMemo, useCallback, Fragment, ReactElement,
} from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router';
import Card from '../../../components/card/card.component';
import Table from '../../../components/table/table.component';
import TableHead from '../../../components/table-head/table-head.component';
import TableBody from '../../../components/table-body/table-body.component';
import TableRow from '../../../components/table-row/table-row.component';
import TableCell from '../../../components/table-cell/table-cell.component';
import TableSortLabel from '../../../components/table-sort-label/table-sort-label.component';
import TablePagination from '../../../components/table-pagination/table-pagination.component';
import CheckAccess from '../../../components/check-access/check-access.component';
import StatusBar from '../../../components/status-bar/status-bar.component';
import List from '../../../components/list/list.component';
import ListItem from '../../../components/list-item/list-item.component';
import ListItemText from '../../../components/list-item-text/list-item-text.component';
import TextNoWrapBlock from '../../../components/text-no-wrap-block/text-no-wrap-block.component';
import PaddedFlexContainer from '../../../components/padded-flex-container/padded-flex-container.component';
import Image from '../../../components/image/image.component';
import Loader from '../../../components/loader/loader.component';
import ContextMenu from '../../../components/context-menu/context-menu.component';
import FilteredLink from '../../../components/filtered-link/filtered-link.component';
import ChipLink from '../../../components/chip-link/chip-link.component';
import Tooltip from '../../../components/tooltip/tooltip.component';
import PullToRefresh from '../../../components/pull-to-refresh/pull-to-refresh.component';
import NoContentBlock from '../../../components/no-content-block/no-content-block.component';
import ActionButton from '../../../components/action-button/action-button.component';
import ConfirmModal from '../../../components/confirm-modal/confirm-modal.component';
import ContentHeader from '../../../components/content-header/content-header.component';
import Form from '../../../components/form/form.component';
import FormError from '../../../components/form-error/form-error.component';
import FormContainer from '../../../components/form-container/form-container.component';
import LetterAvatar from '../../../components/letter-avatar/letter-avatar.component';
import TextBlock from '../../../components/text-block/text-block.component';
import TableCellShort from '../../../components/table-cell-short/table-cell-short.component';
import TableCellExtended from '../../../components/table-cell-extended/table-cell-extended.component';
import FilterPanel from '../../../components/filter-panel/filter-panel.component';
import ContentListContainerOffset
  from '../../app/common/content-list-container-offset/content-list-container-offset.component';
import Controls from '../../app/controls/controls.component';
import SettingsPageTabs from '../settings-page-tabs/settings-page-tabs.component';
import SettingsUserModal from './settings-user-modal/settings-user-modal.component';
import SettingsUserListFilters from './settings-user-list-filters/settings-user-list-filters.component';
import SettingsUserInvitationModal
  from './settings-user-invitation-modal/settings-user-invitation-modal.component';
import SendIcon from '../../../icons/send-icon';
import DeleteItemIcon from '../../../icons/delete-item-icon';
import MoreIcon from '../../../icons/more-icon';
import DeactivateIcon from '../../../icons/deactivate-icon';
import ResetPasswordIcon from '../../../icons/reset-password-icon';
import SettingsUserListValidator from './settings-user-list.validator';
import FormatService from '../../../services/format-service';
import GoogleMapService from '../../../services/google-map-service';
import ScreenService from '../../../services/screen-service';
import FormService from '../../../services/form-service';
import StructureService from '../../../services/structure-service';
import useFormStructureModified from '../../../hooks/use-form-structure-modified';
import useServerFormErrors from '../../../hooks/use-server-form-errors';
import useScreenSmall from '../../../hooks/use-screen-small';
import {
  getUserListData, saveUserListData, getUserEmailContactData, resetUserEmailContactData, deleteUser,
  deactivateUser, resetUserPassword,
} from './settings-user-list.action';
import { getSettingsData } from '../settings.action';
import { getListContactCompanyData, getListDealData } from '../../app/search-list/search-list.action';
import { resetFormErrors } from '../../app/app.action';
import { SORT_TYPE_ASC, SORT_TYPE_DESC } from '../../../constants/system/sort-types';
import { PAGE_DEFAULT } from '../../../constants/system/pages';
import { TABLE_LIMIT_DEFAULT } from '../../../constants/system/table-limits';
import { FILTER_VALUE_EMPTY } from '../../../constants/system/filter-values';
import { ROUTE_USER, ROUTE_CONTACT, ROUTE_SETTINGS_USER_LIST } from '../../../constants/system/routes';
import {
  QUERY_PARAM_LIMIT, QUERY_PARAM_TAB, QUERY_PARAM_ACCOUNT_USER_TYPE, QUERY_PARAM_ACTIVE, QUERY_PARAM_ROLE,
  QUERY_PARAM_COMPANY_ID,
} from '../../../constants/system/query-params';
import {
  CONTACT_VIEW, SETTINGS_USER_CREATE, SETTINGS_USER_LIST_VIEW, SETTINGS_USER_INVITE_CREATE, SETTINGS_USER_DELETE,
} from '../../../constants/entity/permissions';
import { FORM_ID_SETTINGS_USER_LIST } from '../../../constants/system/form-ids';
import {
  DATA_IS_ACTIVE, DATA_IS_PASSWORD_CHANGE_REQUIRED, DATA_USER_ID,
} from '../../../constants/system/data-attributes';
import {
  ACCOUNT_USER_TYPE_LIST_DEAL_LIMITED, ACCOUNT_USER_TYPE_LIST_PORTFOLIO_LIMITED,
} from '../../../constants/entity/account-user-type-lists';
import { ACTIVE_TYPE_LIST } from '../../../constants/system/active-types';
import { FILTER_KEY_NAME } from '../../../constants/system/filter-keys';
import {
  FILTER_TYPE_ACCOUNT_USER_TYPE, FILTER_TYPE_ACTIVE, FILTER_TYPE_ROLE, FILTER_TYPE_COMPANY_ID,
} from '../../../constants/system/filter-types';
import { ACCOUNT_USER_TYPE_ADMIN, ACCOUNT_USER_TYPE_SYSTEM_ADMIN } from '../../../constants/entity/account-user-types';
import { TAB_NAME_USERS } from '../../../constants/system/tab-names';
import CompanyDefaultImage from '../../../static/images/company-default-image.png';

const SettingsUserList: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const HEADER_NAME_PHOTO: string = 'photo';

  const {
    isDataLoaded,
    isRightDrawerOpen,
    currentUser,
    userEmailContactData,
    userListData,
    usersTotal,
    roleList,
    assetTypeList,
    accountUserTypeList,
    contactCompanyList,
    dealList,
    portfolioList,
    accessLevelList,
    form: formProps,
    modified: formModifiedFields,
    handleSubmit: submitForm,
    pristine: isFormPristine,
    isSubmitting: isFormSaving,
    error: formError,
    values: formValues,
    hasValidationErrors: withFormErrors,
    formErrors: serverFormErrors,
    mainFormError: mainServerFormError,
  } = props;

  const {
    reset: resetForm,
    change: changeForm,
  } = formProps;

  const navigate: ReactRouterNavigate = useNavigate();
  const location: ReactRouterLocation = useLocation();
  const dispatch: ReduxDispatch = useDispatch();
  const isSmallScreen: boolean = useScreenSmall();
  const isFormStructureModified: boolean = useFormStructureModified(formModifiedFields, isDataLoaded);

  const pageTitle: string = 'Settings: Users';
  const createButtonTitle: string = 'New User';
  const formKeyNew: string = FormService.getIndexedFormFieldKey(FormService.FORM_KEY_NEW);

  const locationQueryParams: ParsedQueryParams | null = FormatService.parseQueryParams(location.search);
  const queryTab: string | null = locationQueryParams && locationQueryParams[QUERY_PARAM_TAB]
    ? FormatService.parseStringParam(locationQueryParams[QUERY_PARAM_TAB].toString()) : null;
  const queryLimit: number | null = locationQueryParams && locationQueryParams[QUERY_PARAM_LIMIT]
    ? FormatService.parseInt(locationQueryParams[QUERY_PARAM_LIMIT].toString()) : null;
  const queryAccountUserType: string | null = locationQueryParams && locationQueryParams[QUERY_PARAM_ACCOUNT_USER_TYPE]
    ? FormatService.parseStringParam(locationQueryParams[QUERY_PARAM_ACCOUNT_USER_TYPE].toString()) : null;
  const queryActive: boolean | null = locationQueryParams && locationQueryParams[QUERY_PARAM_ACTIVE]
    ? FormatService.extractBooleanValue(locationQueryParams[QUERY_PARAM_ACTIVE].toString()) : null;
  const queryCompanyId: number | null = locationQueryParams && locationQueryParams[QUERY_PARAM_COMPANY_ID]
    ? FormatService.parseInt(locationQueryParams[QUERY_PARAM_COMPANY_ID].toString()) : null;
  const queryRole: string | null = locationQueryParams && locationQueryParams[QUERY_PARAM_ROLE]
    ? FormatService.parseStringParam(locationQueryParams[QUERY_PARAM_ROLE].toString()) : null;

  const DEFAULT_SORT: SortParams = useMemo((): SortParams => ({
    field: 'fullName',
    type: SORT_TYPE_ASC,
  }), []);

  const DEFAULT_FILTERS: Record<string, FilterValue> = useMemo((): Record<string, FilterValue> => ({
    [FILTER_TYPE_ACCOUNT_USER_TYPE]: queryAccountUserType || null,
    [FILTER_TYPE_ACTIVE]: queryActive || null,
    [FILTER_TYPE_ROLE]: queryRole || null,
    [FILTER_TYPE_COMPANY_ID]: queryCompanyId || null,
  }), [queryAccountUserType, queryActive, queryRole, queryCompanyId]);

  const FILTER_OPTION_MAP: Record<string, FilterOption[]> = useMemo((): Record<string, FilterOption[]> => ({
    [FILTER_TYPE_ACCOUNT_USER_TYPE]: accountUserTypeList,
    [FILTER_TYPE_ACTIVE]: ACTIVE_TYPE_LIST,
    [FILTER_TYPE_ROLE]: roleList,
    [FILTER_TYPE_COMPANY_ID]: contactCompanyList,
  }), [accountUserTypeList, roleList, contactCompanyList]);

  const FILTER_KEY_MAP: Record<string, string> = useMemo((): Record<string, string> => ({
    [FILTER_TYPE_ACCOUNT_USER_TYPE]: FILTER_KEY_NAME,
    [FILTER_TYPE_ACTIVE]: FILTER_KEY_NAME,
    [FILTER_TYPE_ROLE]: FILTER_KEY_NAME,
  }), []);

  const FILTER_LABEL_MAP: Record<string, string> = useMemo((): Record<string, string> => ({
    [FILTER_TYPE_ACCOUNT_USER_TYPE]: 'Type',
    [FILTER_TYPE_ACTIVE]: 'Active',
    [FILTER_TYPE_ROLE]: 'Role',
    [FILTER_TYPE_COMPANY_ID]: 'Company',
  }), []);

  const FILTER_LABEL_KEY_MAP: Record<string, string> = useMemo((): Record<string, string> => ({}), []);

  const [isUserModalOpen, setIsUserModalOpen]: [boolean, Function] = useState(false);
  const [isOpenMessage, setIsOpenMessage]: [boolean, Function] = useState(false);
  const [statusMessage, setStatusMessage]: [string, Function] = useState('');
  const [userPhotoFiles, setUserPhotoFiles]: [Record<string, File>, Function] = useState({});
  const [userAssetTypes, setUserAssetTypes]: [Record<string, AssetType>, Function] = useState({});
  const [userAddressList, setUserAddressList]: [Record<string, JsonObject> | null, Function]
    = useState<StateValueDefault>(null);
  const [sortParams, setSortParams]: [SortParams, Function] = useState(DEFAULT_SORT);
  const [limit, setLimit]: [number, Function] = useState(queryLimit || TABLE_LIMIT_DEFAULT);
  const [page, setPage]: [number, Function] = useState(PAGE_DEFAULT);
  const [menuAnchor, setMenuAnchor]: [HTMLElement | null, Function] = useState(null);
  const [filters, setFilters]: [Record<string, FilterValue>, Function] = useState(DEFAULT_FILTERS);
  const [currentFilters, setCurrentFilters]: [Record<string, FilterValue>, Function] = useState(DEFAULT_FILTERS);
  const [isUserInvitationModalOpen, setIsUserInvitationModalOpen]: [boolean, Function] = useState(false);
  const [isDeleteConfirmModalOpen, setIsDeleteConfirmModalOpen]: [boolean, Function] = useState(false);
  const [isDeactivateUserConfirmModalOpen, setIsDeactivateUserConfirmModalOpen]: [boolean, Function] = useState(false);
  const [isResetPasswordConfirmModalOpen, setIsResetPasswordConfirmModalOpen]: [boolean, Function] = useState(false);
  const [isUserLinked, setIsUserLinked]: [boolean, Function] = useState(false);
  const [userActiveId, setUserActiveId]: [number | null, Function] = useState(null);
  const [confirmModalErrorMessage, setConfirmModalErrorMessage]: [string | null, Function] = useState(null);

  const settingsUserListViewPermissions: string[] = useMemo((): string[] => [SETTINGS_USER_LIST_VIEW], []);
  const saveFormDeps: DepsItem[] = useMemo((): DepsItem[] => [formValues], [formValues]);

  const isCurrentUserSystemAdmin: boolean = !!currentUser && !!currentUser.accountUserType
    && currentUser.accountUserType === ACCOUNT_USER_TYPE_SYSTEM_ADMIN;
  const accountUserTypeMap: Record<string, AccountUserType> = useMemo((): Record<string, AccountUserType> => (
    StructureService.getItemMap(accountUserTypeList, 'name')
  ), [accountUserTypeList]);
  const filteredAccountUserTypeList: AccountUserType[] = useMemo((): AccountUserType[] => (
    accountUserTypeList && (
      !isCurrentUserSystemAdmin ? accountUserTypeList.filter((accountUserTypeItem: AccountUserType): boolean => (
        accountUserTypeItem.name !== ACCOUNT_USER_TYPE_ADMIN
      )) : accountUserTypeList
    )
  ), [accountUserTypeList, isCurrentUserSystemAdmin]);

  const isFormDataPristine: boolean = isFormPristine && !Object.keys(userPhotoFiles).length
    && !Object.keys(userAssetTypes).length;
  const isSaveDisabled: boolean = isFormSaving || (isFormDataPristine && !isFormStructureModified);
  const withAllFiltersEmpty: boolean = !filters[FILTER_TYPE_ACCOUNT_USER_TYPE] && !filters[FILTER_TYPE_ACTIVE]
    && filters[FILTER_TYPE_ACTIVE] !== false && !filters[FILTER_TYPE_ROLE] && !filters[FILTER_TYPE_COMPANY_ID];
  const withFilters: boolean = !withAllFiltersEmpty;
  const deleteConfirmModalTitle: string = 'Delete User';
  const deleteConfirmModalText: string = 'Are you sure you want to delete this user?';
  const deactivateUserConfirmModalTitle: string = 'Deactivate User';
  const deactivateUserConfirmModalText: string = 'Are you sure you want to deactivate this user?';
  const resetPasswordConfirmModalTitle: string = 'Reset Password';
  const resetPasswordConfirmModalText: string = 'Are you sure you want to require password change '
    + 'on login for this user?';
  const withServerErrors: boolean = serverFormErrors && !!Object.keys(serverFormErrors).length;

  const onDealSearch: Function = useCallback((query: string): void => {
    dispatch(getListDealData({
      query,
    }));
  }, []);

  const onDealSearchClose: Function = useCallback((): void => {
    dispatch(getListDealData({
      query: null,
    }));
  }, []);

  const onContactCompanySearch: Function = useCallback((query: string): void => {
    dispatch(getListContactCompanyData({
      query,
    }));
  }, []);

  const onContactCompanySearchClose: Function = useCallback((): void => {
    dispatch(getListContactCompanyData({
      query: null,
    }));
  }, []);

  const openUserInvitationModal: Function = useCallback((userId: number): void => {
    setIsUserInvitationModalOpen(true);
    setUserActiveId(userId);
  }, []);

  const closeUserInvitationModal: Function = useCallback((): void => {
    setIsUserInvitationModalOpen(false);
    setUserActiveId(null);
  }, []);

  const openUserDeleteConfirmModal: Function = useCallback((userId: number): void => {
    setUserActiveId(userId);
    setIsDeleteConfirmModalOpen(true);
  }, []);

  const closeUserDeleteConfirmModal: Function = useCallback((): void => {
    setIsDeleteConfirmModalOpen(false);
    setConfirmModalErrorMessage(null);
    setUserActiveId(null);
  }, []);

  const openUserDeactivateConfirmModal: Function = useCallback((userId: number): void => {
    setUserActiveId(userId);
    setIsDeactivateUserConfirmModalOpen(true);
  }, []);

  const closeUserDeactivateConfirmModal: Function = useCallback((): void => {
    setIsDeactivateUserConfirmModalOpen(false);
    setConfirmModalErrorMessage(null);
    setUserActiveId(null);
  }, []);

  const openUserResetPasswordConfirmModal: Function = useCallback((userId: number): void => {
    setUserActiveId(userId);
    setIsResetPasswordConfirmModalOpen(true);
  }, []);

  const closeUserResetPasswordConfirmModal: Function = useCallback((): void => {
    setIsResetPasswordConfirmModalOpen(false);
    setConfirmModalErrorMessage(null);
    setUserActiveId(null);
  }, []);

  const actionMenuItems: ActionMenuItem[] = useMemo((): ActionMenuItem[] => [
    {
      id: 1,
      icon: <ResetPasswordIcon />,
      text: 'Reset Password',
      isVisibleByExtraEntityTypeValue: FormatService.BOOLEAN_VALUE_TRUE,
      onClick: (event: React.MouseEvent<Element, MouseEvent>, userId: number): void => {
        if (userId) {
          openUserResetPasswordConfirmModal(userId);
        }
      },
    },
    {
      id: 2,
      icon: <DeactivateIcon />,
      text: 'Deactivate',
      isVisibleByEntityTypeValue: FormatService.BOOLEAN_VALUE_TRUE,
      onClick: (event: React.MouseEvent<Element, MouseEvent>, userId: number): void => {
        if (userId) {
          openUserDeactivateConfirmModal(userId);
        }
      },
    },
    {
      id: 3,
      icon: <SendIcon />,
      text: 'Send Invitation',
      permission: SETTINGS_USER_INVITE_CREATE,
      onClick: (event: React.MouseEvent<Element, MouseEvent>, userId: number): void => {
        if (userId) {
          openUserInvitationModal(userId);
        }
      },
    },
    {
      id: 4,
      icon: <DeleteItemIcon />,
      text: 'Delete',
      permission: SETTINGS_USER_DELETE,
      onClick: (event: React.MouseEvent<Element, MouseEvent>, userId: number): void => {
        if (userId) {
          openUserDeleteConfirmModal(userId);
        }
      },
    },
  ], []);

  const openActionMenu: MouseEventHandlerFunction = useCallback((
    event: React.MouseEvent<Element, MouseEvent>,
  ): void => {
    setMenuAnchor(event.currentTarget);

    event.stopPropagation();
  }, []);

  const onContactClick: MouseEventItemHandlerFunction = useCallback((
    event: React.MouseEvent<Element, MouseEvent>,
    contactId: number,
  ): void => {
    navigate({
      pathname: `${ROUTE_CONTACT}/${contactId}`,
    });

    event.stopPropagation();
  }, []);

  const tableUserListFields: TableField[] = useMemo((): TableField[] => [
    {
      id: 1,
      name: 'photo',
      title: 'Photo',
      isSortable: false,
      isNumber: false,
      component: (user: User): ReactElement => (
        <TableCellShort isTextCenter>
          {
            (!!user.photoThumbnail || !!user.photo) && (
              <PaddedFlexContainer isCentered>
                <Image isCentered text={user.fullName} link={user.photoThumbnail || user.photo} />
              </PaddedFlexContainer>
            )
          }
          {
            (!user.photoThumbnail && !user.photo) && (
              <LetterAvatar wordList={[user.firstName, user.lastName]} />
            )
          }
        </TableCellShort>
      ),
    },
    {
      id: 2,
      name: 'fullName',
      title: 'Full Name',
      isSticky: true,
      isSortable: true,
      isNumber: false,
      component: (user: User): ReactElement => (
        <TableCell isText isSticky isStickyLeft>
          {user.fullName}
        </TableCell>
      ),
    },
    {
      id: 3,
      name: 'accountUserType',
      title: 'Type',
      isSortable: true,
      isNumber: false,
      component: (user: User): ReactElement => {
        const userAccountUserType: AccountUserType | null = user.accountUserType
          ? accountUserTypeMap[user.accountUserType] : null;

        return (
          <TableCellExtended isText>
            {
              userAccountUserType && userAccountUserType.group && (
                <Fragment>
                  {userAccountUserType.group.title}
                  :
                  {' '}
                  {userAccountUserType.title}
                </Fragment>
              )
            }
          </TableCellExtended>
        );
      },
    },
    {
      id: 4,
      name: 'company.name',
      title: 'Company',
      isSortable: true,
      isNumber: false,
      component: (user: User): ReactElement => (
        <TableCell isText>
          {
            user.company && (
              <ChipLink
                label={user.company.name}
                imageLink={user.company.photoThumbnail || user.company.photo || CompanyDefaultImage}
                permission={CONTACT_VIEW}
                onClick={(event: React.MouseEvent<Element, MouseEvent>): void => {
                  onContactClick(event, user.company.id);
                }}
              />
            )
          }
        </TableCell>
      ),
    },
    {
      id: 5,
      name: 'phone',
      title: 'Phone',
      isSortable: true,
      isNumber: false,
      component: (user: User): ReactElement => (
        <TableCell isText>
          {
            user.phone && user.phoneText && (
              <FilteredLink
                isPhone
                link={FormatService.formatCleanIntegerNumberString(user.phone)}
                phoneExtension={user.phoneExtension}
              >
                {`${user.phoneText}${user.phoneExtension ? ` ext. ${user.phoneExtension}` : ''}`}
              </FilteredLink>
            )
          }
        </TableCell>
      ),
    },
    {
      id: 6,
      name: 'phoneWork',
      title: 'Work Phone',
      isSortable: true,
      isNumber: false,
      component: (user: User): ReactElement => (
        <TableCell isText>
          {
            user.phoneWork && user.phoneWorkText && (
              <FilteredLink
                isPhone
                link={FormatService.formatCleanIntegerNumberString(user.phoneWork)}
                phoneExtension={user.phoneWorkExtension}
              >
                {`${user.phoneWorkText}${user.phoneWorkExtension ? ` ext. ${user.phoneWorkExtension}` : ''}`}
              </FilteredLink>
            )
          }
        </TableCell>
      ),
    },
    {
      id: 7,
      name: 'email',
      title: 'Email',
      isSortable: true,
      isNumber: false,
      component: (user: User): ReactElement => (
        <TableCell>
          {
            user.email && (
              <TextNoWrapBlock>
                <Tooltip title={user.email}>
                  <FilteredLink isEmail link={user.email}>
                    {user.email}
                  </FilteredLink>
                </Tooltip>
              </TextNoWrapBlock>
            )
          }
        </TableCell>
      ),
    },
    {
      id: 8,
      name: 'streetAddress',
      title: 'Address',
      isSortable: false,
      isNumber: false,
      component: (user: User): ReactElement => (
        <TableCell isText>
          {
            user.streetAddress && (
              <FilteredLink isExternal link={user.address && user.address.url}>
                {user.streetAddress}
              </FilteredLink>
            )
          }
        </TableCell>
      ),
    },
    {
      id: 9,
      name: 'roleText',
      title: 'Role',
      isSortable: true,
      isNumber: false,
      component: (user: User): ReactElement => (
        <TableCell isText>
          {user.roleText}
        </TableCell>
      ),
    },
    {
      id: 10,
      name: 'title',
      title: 'Title',
      isSortable: true,
      isNumber: false,
      component: (user: User): ReactElement => (
        <TableCell isText>
          {user.title}
        </TableCell>
      ),
    },
    {
      id: 11,
      name: 'active',
      title: 'Active',
      isSortable: true,
      isNumber: false,
      component: (user: User): ReactElement => (
        <TableCell isText>
          {FormatService.formatBooleanValue(user.active)}
        </TableCell>
      ),
    },
    {
      id: 12,
      name: 'lastLoginDate',
      title: 'Last Login Date',
      isSortable: true,
      isNumber: false,
      component: (user: User): ReactElement => (
        <TableCellExtended isText>
          {user.lastLoginDate}
        </TableCellExtended>
      ),
    },
    {
      id: 13,
      name: 'lastActiveDate',
      title: 'Last Activity Date',
      isSortable: true,
      isNumber: false,
      component: (user: User): ReactElement => (
        <TableCellExtended isText>
          {user.lastActiveDate}
        </TableCellExtended>
      ),
    },
    {
      id: 14,
      name: 'actions',
      title: 'Actions',
      isSortable: false,
      isNumber: false,
      isHeaderNameHidden: true,
      isSticky: true,
      isStickyRight: true,
      component: (user: User): ReactElement => (
        <TableCell isActions isTextRight isSticky isStickyRight>
          <ActionButton
            withPopup
            withSmallPadding
            onClick={openActionMenu}
            {...{
              [DATA_USER_ID]: user.id,
              [DATA_IS_ACTIVE]: user.active ? FormatService.BOOLEAN_VALUE_TRUE : null,
              [DATA_IS_PASSWORD_CHANGE_REQUIRED]: user.isPasswordChangeRequired
                ? FormatService.BOOLEAN_VALUE_TRUE : null,
            }}
          >
            <MoreIcon />
          </ActionButton>
        </TableCell>
      ),
    },
  ], []);

  const onCancelForm: Function = useCallback((): void => {
    FormService.forceResetForm(resetForm, formProps);

    setUserPhotoFiles({});
    setUserAssetTypes({});
    setUserAddressList(null);
    setIsUserLinked(false);
    dispatch(resetUserEmailContactData());
  }, []);

  const openModal: Function = useCallback((): void => {
    setIsUserModalOpen(true);
  }, []);

  const closeModal: Function = useCallback((): void => {
    setIsUserModalOpen(false);
  }, []);

  const getOptions: Function = useCallback((
    pageOption: number,
    limitOption: number,
    filtersOption?: Record<string, FilterValue>,
    sortOption?: SortParams,
  ): RequestOptions => {
    let options: RequestOptions = {
      page: pageOption + 1,
      limit: limitOption,
      filters: { ...filtersOption },
    };

    options = sortOption ? {
      ...options,
      sort: sortOption,
    } : options;

    return options;
  }, []);

  const sendForm: Function = useCallback((values: FormValues): Promise<ActionCallback> | null => {
    if (!values) {
      return null;
    }

    const formData: FormValueItem = {};

    if (values.userListData[formKeyNew]) {
      formData[formKeyNew] = {
        ...values.userListData[formKeyNew],
      };
    }

    if (!Object.keys(formData).length) {
      return null;
    }

    const userList: UserInput[] = [];
    Object.keys(formData).forEach((userId: string): void => {
      const userFormItem: FormValueItem = {
        ...formData[userId],
      };
      const contactCompany: Contact | null = userFormItem.companyId && contactCompanyList && contactCompanyList.length
        ? (contactCompanyList.find((contactCompanyItem: Contact): boolean => (
          userFormItem.companyId === contactCompanyItem.id
        )) || null) : null;

      if (!userFormItem.password || userFormItem.withSendInvitation) {
        delete userFormItem.password;
      }

      delete userFormItem.passwordConfirm;
      delete userFormItem.location;
      delete userFormItem.streetAddress;
      delete userFormItem.postalCode;
      delete userFormItem.accountUserTypeGroup;

      userFormItem.assetTypes = userFormItem.assetTypes && userFormItem.assetTypes.length
        ? userFormItem.assetTypes.map((assetTypeItem: AssetType): number => assetTypeItem.id) : [];
      userFormItem.accountUserDeals = userFormItem.accountUserDeals && userFormItem.accountUserDeals.length
        ? userFormItem.accountUserDeals.map((dealItem: Deal): number => dealItem.id) : [];
      userFormItem.accountUserPortfolios = userFormItem.accountUserPortfolios
      && userFormItem.accountUserPortfolios.length
        ? userFormItem.accountUserPortfolios.map((portfolioItem: Portfolio): number => portfolioItem.id) : [];
      userFormItem.accountUserDealAccessLevels = userFormItem.accountUserDealAccessLevels
      && Object.keys(userFormItem.accountUserDealAccessLevels).length
        ? Object.keys(userFormItem.accountUserDealAccessLevels).map((
          dealId: string,
        ): AccountUserDealAccessLevelInput => ({
          dealId: FormatService.parseInt(dealId),
          accessLevel: userFormItem.accountUserDealAccessLevels[dealId] || null,
        })).filter((accountUserDealAccessLevel: AccountUserDealAccessLevelInput): boolean => (
          userFormItem.accountUserDeals.includes(accountUserDealAccessLevel.dealId)
        )) : [];
      userFormItem.phone = userFormItem.phone && FormatService.isValidPhoneNumber(userFormItem.phone, true)
        ? FormatService.formatPhoneValue(userFormItem.phone) : null;
      userFormItem.phoneExtension = userFormItem.phoneExtension
        ? FormatService.parseInt(userFormItem.phoneExtension) : null;
      userFormItem.phoneWork = userFormItem.phoneWork && FormatService.isValidPhoneNumber(userFormItem.phoneWork, true)
        ? FormatService.formatPhoneValue(userFormItem.phoneWork) : null;
      userFormItem.phoneWorkExtension = userFormItem.phoneWorkExtension
        ? FormatService.parseInt(userFormItem.phoneWorkExtension) : null;
      userFormItem.withSendInvitation = userFormItem.withSendInvitation || false;
      userFormItem.invitationMessage = userFormItem.invitationMessage || null;
      userFormItem.companyId = contactCompany ? contactCompany.id : (userFormItem.companyId || null);

      if (userPhotoFiles[userId]) {
        userFormItem.photo = userPhotoFiles[userId];
      } else {
        delete userFormItem.photo;
      }

      userList.push(userFormItem);
    });

    return new Promise((resolve: ActionResolveCallback): void => {
      dispatch(saveUserListData({
        userListForm: {
          userList,
        },
        ...getOptions(PAGE_DEFAULT, limit, filters, sortParams),
      }, (response: ActionResponse): void => {
        closeModal();

        onCancelForm();

        setStatusMessage('Saved successfully');
        setIsOpenMessage(true);

        resolve(response);
      }, (exception: ServerError): void => {
        if (exception && exception.message) {
          submitForm();
        }
      }));
    });
  }, [isSaveDisabled, contactCompanyList, userPhotoFiles, limit, filters, sortParams, formKeyNew]);

  const onSubmitForm: Function = useCallback((): void => {
    submitForm();

    if (!withFormErrors) {
      sendForm(formValues);
    } else {
      FormService.focusOnFirstFormError();
    }
  }, [
    isSaveDisabled, contactCompanyList, userPhotoFiles, limit, filters, sortParams, formKeyNew, formValues,
    withFormErrors,
  ]);

  const onUserSubmitCreateForm: Function = useCallback((
    event: React.MouseEvent<Element, MouseEvent>, onSubmitCallback: Function,
  ): void => {
    submitForm(event);

    if (!withFormErrors) {
      onSubmitCallback();
    }
  }, [
    isSaveDisabled, contactCompanyList, userPhotoFiles, limit, filters, sortParams, formKeyNew, formValues,
    withFormErrors,
  ]);

  const onUserCreate: Function = useCallback((event: React.MouseEvent<Element, MouseEvent>): void => {
    onSubmitForm(event);
  }, [isSaveDisabled, contactCompanyList, userPhotoFiles, limit, filters, sortParams, formValues, withFormErrors]);

  const onDeleteConfirm: Function = useCallback((): Promise<ActionCallback> | null => {
    if (!userActiveId) {
      return null;
    }

    return new Promise((resolve: ActionResolveCallback): void => {
      dispatch(deleteUser({
        userId: userActiveId,
        isListItem: true,
        ...getOptions(page, limit, filters, sortParams),
      }, (response: ActionResponse): void => {
        setIsDeleteConfirmModalOpen(false);
        setConfirmModalErrorMessage(null);

        setUserActiveId(null);

        setStatusMessage('Deleted successfully');
        setIsOpenMessage(true);

        resolve(response);
      }, (exception: ServerError): void => {
        if (exception && exception.message) {
          setConfirmModalErrorMessage(exception.message);
        }
      }));
    });
  }, [userActiveId, page, limit, filters, sortParams]);

  const onDeactivateUserConfirm: Function = useCallback((): Promise<ActionCallback> | null => {
    if (!userActiveId) {
      return null;
    }

    return new Promise((resolve: ActionResolveCallback): void => {
      dispatch(deactivateUser({
        userId: userActiveId,
        ...getOptions(page, limit, filters, sortParams),
      }, (response: ActionResponse): void => {
        setIsDeactivateUserConfirmModalOpen(false);
        setConfirmModalErrorMessage(null);

        setUserActiveId(null);

        setStatusMessage('Deactivated successfully');
        setIsOpenMessage(true);

        resolve(response);
      }, (exception: ServerError): void => {
        if (exception && exception.message) {
          setConfirmModalErrorMessage(exception.message);
        }
      }));
    });
  }, [userActiveId, page, limit, filters, sortParams]);

  const onResetPasswordUserConfirm: Function = useCallback((): Promise<ActionCallback> | null => {
    if (!userActiveId) {
      return null;
    }

    return new Promise((resolve: ActionResolveCallback): void => {
      dispatch(resetUserPassword({
        userId: userActiveId,
        ...getOptions(page, limit, filters, sortParams),
      }, (response: ActionResponse): void => {
        setIsResetPasswordConfirmModalOpen(false);
        setConfirmModalErrorMessage(null);

        setUserActiveId(null);

        setStatusMessage('Password reset successfully');
        setIsOpenMessage(true);

        resolve(response);
      }, (exception: ServerError): void => {
        if (exception && exception.message) {
          setConfirmModalErrorMessage(exception.message);
        }
      }));
    });
  }, [userActiveId, page, limit, filters, sortParams]);

  const onUserPhotoChange: Function = useCallback((userId: number, file: File): void => {
    const formKeyUser: string = FormService.getIndexedFormFieldKey(userId);

    setUserPhotoFiles({
      ...userPhotoFiles,
      [userId]: file,
    });
    changeForm(`userListData[${formKeyUser}].photo`, file);
  }, [userPhotoFiles]);

  const onUserPhotoDelete: Function = useCallback((userId: number): void => {
    const formKeyUser: string = FormService.getIndexedFormFieldKey(userId);

    const newUserPhotoFiles: Record<string, File> = userPhotoFiles;
    delete newUserPhotoFiles[userId];
    setUserPhotoFiles({ ...newUserPhotoFiles });

    changeForm(`userListData[${formKeyUser}].photo`, null);
  }, [userPhotoFiles]);

  const onAddressSearchCompleted: Function = useCallback((addressObject: JsonObject, userId: number): void => {
    if (userId || userId === FormService.FORM_KEY_NEW) {
      const formKeyUser: string = FormService.getIndexedFormFieldKey(userId);

      setUserAddressList({
        ...userAddressList,
        [userId]: addressObject,
      });

      changeForm(`userListData[${formKeyUser}].address`, addressObject);
      changeForm(`userListData[${formKeyUser}].location`, GoogleMapService.extractLocation(addressObject));
      changeForm(`userListData[${formKeyUser}].postalCode`, GoogleMapService.extractPostalCode(addressObject));
      changeForm(`userListData[${formKeyUser}].streetAddress`, GoogleMapService.extractStreetAddress(addressObject));
    }
  }, [userAddressList]);

  const onAddressFieldKeyDown: Function = useCallback((userId: number): void => {
    const formKeyUser: string = FormService.getIndexedFormFieldKey(userId);

    if (userAddressList && userAddressList[userId]) {
      setUserAddressList({
        ...userAddressList,
        [userId]: null,
      });

      changeForm(`userListData[${formKeyUser}].address`, '');
      changeForm(`userListData[${formKeyUser}].location`, '');
      changeForm(`userListData[${formKeyUser}].postalCode`, '');
      changeForm(`userListData[${formKeyUser}].streetAddress`, '');
    }
  }, [userAddressList]);

  const closeMessage: Function = useCallback((): void => {
    setIsOpenMessage(false);
    setStatusMessage('');
  }, []);

  const onRowClick: MouseEventItemHandlerFunction = useCallback((
    event: React.MouseEvent<Element, MouseEvent>,
    userItem: User,
  ): void => {
    const withNoSelection: boolean = ScreenService.checkNoSelection();

    if (withNoSelection && userItem.id) {
      navigate({
        pathname: `${ROUTE_USER}/${userItem.id}`,
      });
    }
  }, []);

  const onCreateButtonClick: Function = useCallback((): void => {
    openModal();
  }, []);

  const onAssetTypesChange: Function = useCallback((userId: number, assetTypeValues: AssetType[]): void => {
    const formKeyUser: string = FormService.getIndexedFormFieldKey(userId);

    setUserAssetTypes({
      ...userAssetTypes,
      [userId]: assetTypeValues,
    });

    changeForm(`userListData[${formKeyUser}].assetTypes`, assetTypeValues);
  }, [userAssetTypes]);

  const onPhoneChange: Function = useCallback((userId: number, phoneValue?: string): void => {
    const formKeyUser: string = FormService.getIndexedFormFieldKey(userId);

    changeForm(`userListData[${formKeyUser}].phone`, phoneValue);
  }, []);

  const onPhoneWorkChange: Function = useCallback((userId: number, phoneWorkValue?: string): void => {
    const formKeyUser: string = FormService.getIndexedFormFieldKey(userId);

    changeForm(`userListData[${formKeyUser}].phone`, phoneWorkValue);
  }, []);

  const onEmailChange: Function = useCallback((emailValue: string): void => {
    dispatch(getUserEmailContactData({
      email: emailValue,
    }));
  }, []);

  const onLinkContact: Function = useCallback((userId: number, isLinked?: boolean, contactId?: number): void => {
    const formKeyUser: string = FormService.getIndexedFormFieldKey(userId);

    setIsUserLinked(isLinked);

    if (isLinked) {
      if (contactId) {
        changeForm(`userListData[${formKeyUser}].contactId`, contactId);
      }

      if (userEmailContactData.firstName) {
        changeForm(`userListData[${formKeyUser}].firstName`, userEmailContactData.firstName);
      }

      if (userEmailContactData.lastName) {
        changeForm(`userListData[${formKeyUser}].lastName`, userEmailContactData.lastName);
      }

      if (userEmailContactData.phone) {
        changeForm(`userListData[${formKeyUser}].phone`, userEmailContactData.phone);
        changeForm(`userListData[${formKeyUser}].phoneExtension`, userEmailContactData.phoneExtension || null);
      }

      if (userEmailContactData.phoneWork) {
        changeForm(`userListData[${formKeyUser}].phoneWork`, userEmailContactData.phoneWork);
        changeForm(`userListData[${formKeyUser}].phoneWorkExtension`, userEmailContactData.phoneWorkExtension || null);
      }

      if (userEmailContactData.email) {
        changeForm(`userListData[${formKeyUser}].email`, userEmailContactData.email);
      }

      if (userEmailContactData.company && userEmailContactData.company.id) {
        changeForm(`userListData[${formKeyUser}].companyId`, userEmailContactData.company.id);
      }

      if (userEmailContactData.address) {
        changeForm(`userListData[${formKeyUser}].address`, userEmailContactData.address);
        changeForm(
          `userListData[${formKeyUser}].location`,
          GoogleMapService.extractLocation(userEmailContactData.address),
        );
        changeForm(
          `userListData[${formKeyUser}].postalCode`,
          GoogleMapService.extractPostalCode(userEmailContactData.address),
        );
        changeForm(
          `userListData[${formKeyUser}].streetAddress`,
          GoogleMapService.extractStreetAddress(userEmailContactData.address),
        );

        setUserAddressList({
          ...userAddressList,
          [userId]: userEmailContactData.address,
        });
      }

      if (userEmailContactData.addressDetails) {
        changeForm(`userListData[${formKeyUser}].addressDetails`, userEmailContactData.addressDetails);
      }

      if (userEmailContactData.description) {
        changeForm(`userListData[${formKeyUser}].bio`, userEmailContactData.description);
      }

      if (userEmailContactData.assetTypes && userEmailContactData.assetTypes.length) {
        changeForm(`userListData[${formKeyUser}].assetTypes`, userEmailContactData.assetTypes);

        setUserAssetTypes({
          ...userAssetTypes,
          [formKeyUser]: userEmailContactData.assetTypes,
        });
      }

      if (userEmailContactData.title) {
        changeForm(`userListData[${formKeyUser}].title`, userEmailContactData.title);
      }
    }
  }, [userEmailContactData, userAddressList, userAssetTypes]);

  const onAccountUserTypeChange: Function = useCallback((
    userId: number, accountUserTypeName: string, accountUserTypeGroupName: string,
  ): void => {
    const formKeyUser: string = FormService.getIndexedFormFieldKey(userId);

    if (!ACCOUNT_USER_TYPE_LIST_DEAL_LIMITED.includes(accountUserTypeName)) {
      changeForm(`userListData[${formKeyUser}].accountUserDeals`, []);
    }

    if (!ACCOUNT_USER_TYPE_LIST_PORTFOLIO_LIMITED.includes(accountUserTypeName)) {
      changeForm(`userListData[${formKeyUser}].accountUserPortfolios`, []);
    }

    changeForm(`userListData[${formKeyUser}].accountUserTypeGroup`, accountUserTypeGroupName || null);
  }, []);

  const onAccountUserPortfoliosChange: Function = useCallback((userId: number, portfolioValues: Portfolio[]): void => {
    const formKeyUser: string = FormService.getIndexedFormFieldKey(userId);

    changeForm(`userListData[${formKeyUser}].accountUserPortfolios`, portfolioValues);
    changeForm(`userListData[${formKeyUser}].accountUserDealAccessLevels`, null);
  }, []);

  const onAccountUserDealsChange: Function = useCallback((
    userId: number,
    dealValues: Deal[],
    removedDealValues: Deal[],
  ): void => {
    const formKeyUser: string = FormService.getIndexedFormFieldKey(userId);

    changeForm(`userListData[${formKeyUser}].accountUserDeals`, dealValues);

    if (removedDealValues && removedDealValues.length) {
      removedDealValues.forEach((dealItem: Deal): void => {
        const formKeyDeal: string = FormService.getIndexedFormFieldKey(dealItem.id);

        changeForm(`userListData[${formKeyUser}].accountUserDealAccessLevels[${formKeyDeal}]`, null);
      });
    }
  }, []);

  const onAccountUserDealAccessLevelChange: Function = useCallback((
    userId: number, dealId: number, accessLevelValue: string,
  ): void => {
    const formKeyUser: string = FormService.getIndexedFormFieldKey(userId);
    const formKeyDeal: string = FormService.getIndexedFormFieldKey(dealId);

    changeForm(`userListData[${formKeyUser}].accountUserDealAccessLevels[${formKeyDeal}]`, accessLevelValue);
  }, []);

  const onSendInvitationCheckboxChange: Function = useCallback((userId: number): void => {
    const formKeyUser: string = FormService.getIndexedFormFieldKey(userId);

    changeForm(`userListData[${formKeyUser}].password`, null);
    changeForm(`userListData[${formKeyUser}].passwordConfirm`, null);
  }, []);

  const onPageChange: ChangePageHandlerFunction = useCallback((
    event: React.MouseEvent<Element, MouseEvent> | null,
    pageValue: number,
  ): void => {
    setPage(pageValue);
    ScreenService.resetScrollPosition();
    dispatch(getUserListData(getOptions(pageValue, limit, filters, sortParams)));
  }, [limit, filters, sortParams]);

  const onLimitChange: ChangeLimitHandlerFunction = useCallback((event: React.ChangeEvent<HTMLInputElement>): void => {
    const limitValue: string = event.target.value;

    setPage(PAGE_DEFAULT);
    setLimit(limitValue);

    const queryParams: LocationQueryParams = locationQueryParams || {};

    if (queryTab) {
      queryParams[QUERY_PARAM_TAB] = queryTab;
    }

    queryParams[QUERY_PARAM_LIMIT] = limitValue;

    if (filters) {
      Object.entries(filters).forEach(([filterTypeItem, valueItem]: [string, FilterValue]): void => {
        const filterUrlName: string = FormatService.formatCamelCaseToKebabCase(filterTypeItem);

        if (valueItem) {
          queryParams[filterUrlName] = encodeURIComponent(valueItem);
        } else {
          delete queryParams[filterUrlName];
        }
      });
    }

    dispatch(getUserListData(getOptions(PAGE_DEFAULT, limitValue, filters, sortParams)));

    navigate({
      pathname: ROUTE_SETTINGS_USER_LIST,
      search: FormatService.formatQueryParams(queryParams),
    });
  }, [locationQueryParams, queryTab, filters, sortParams]);

  const sortTableRows: Function = useCallback((columnName: string): void => {
    setPage(PAGE_DEFAULT);
    setSortParams({
      field: columnName,
      type: sortParams.field === columnName && sortParams.type === SORT_TYPE_DESC ? SORT_TYPE_ASC : SORT_TYPE_DESC,
    });
  }, [sortParams]);

  const closeActionMenu: MouseEventHandlerFunction = useCallback((
    event: React.MouseEvent<Element, MouseEvent>,
  ): void => {
    setMenuAnchor(null);

    event.stopPropagation();
  }, []);

  const onCheckIsMenuItemVisible: Function = useCallback((
    actionMenuItem: ActionMenuItem, linkedUserActive: string | null, isPasswordChangeRequired: string | null,
  ): boolean => (
    ((actionMenuItem.isVisibleByEntityTypeValue && linkedUserActive
      && FormatService.extractBooleanValue(linkedUserActive)) || !actionMenuItem.isVisibleByEntityTypeValue)
    && ((actionMenuItem.isVisibleByExtraEntityTypeValue && isPasswordChangeRequired
        && !FormatService.extractBooleanValue(isPasswordChangeRequired))
      || !actionMenuItem.isVisibleByExtraEntityTypeValue)
  ), []);

  const setFilterValue: Function = useCallback((filterType: string, value: string): Record<string, FilterValue> => {
    const selectedValue: FilterValue = FormatService.parseInt(value) !== FILTER_VALUE_EMPTY ? value : null;
    const newFilters: Record<string, FilterValue> = {
      ...currentFilters,
      [filterType]: selectedValue,
    };
    setCurrentFilters(newFilters);

    return newFilters;
  }, [currentFilters]);

  const onResetFilters: Function = useCallback((): void => {
    setCurrentFilters({ ...filters });
  }, [filters]);

  const onApplyFilters: Function = useCallback((newFilters: Record<string, FilterValue>): void => {
    const resultNewFilters: Record<string, FilterValue> = newFilters || currentFilters;

    setFilters({ ...resultNewFilters });
    setPage(PAGE_DEFAULT);

    dispatch(getUserListData(getOptions(PAGE_DEFAULT, limit, resultNewFilters, sortParams)));

    const queryParams: LocationQueryParams = locationQueryParams || {};

    if (queryTab) {
      queryParams[QUERY_PARAM_TAB] = queryTab;
    }

    if (queryLimit) {
      queryParams[QUERY_PARAM_LIMIT] = queryLimit;
    }

    if (resultNewFilters) {
      Object.entries(resultNewFilters).forEach(([filterTypeItem, valueItem]: [string, FilterValue]): void => {
        const filterUrlName: string = FormatService.formatCamelCaseToKebabCase(filterTypeItem);

        if (valueItem || valueItem === false) {
          queryParams[filterUrlName] = encodeURIComponent(valueItem);
        } else {
          delete queryParams[filterUrlName];
        }
      });
    }

    navigate({
      pathname: ROUTE_SETTINGS_USER_LIST,
      search: FormatService.formatQueryParams(queryParams),
    });
  }, [locationQueryParams, currentFilters, limit, sortParams, queryTab, queryLimit]);

  const onDeleteFilterValue: Function = useCallback((filterType: string): void => {
    const newFilters: Record<string, FilterValue> = setFilterValue(filterType, null);

    onApplyFilters(newFilters);
  }, [locationQueryParams, currentFilters, limit, sortParams, queryTab, queryLimit]);

  const applyUrlFilters: Function = useCallback((): Record<string, FilterValue> => {
    const filterValues: Record<string, FilterValue> = { ...filters };

    if (queryActive || queryActive === false) {
      filterValues[FILTER_TYPE_ACTIVE] = queryActive;
    }

    if (queryRole) {
      filterValues[FILTER_TYPE_ROLE] = queryRole;
    }

    if (queryAccountUserType) {
      filterValues[FILTER_TYPE_ACCOUNT_USER_TYPE] = queryAccountUserType;
    }

    setFilters(filterValues);

    return filterValues;
  }, [filters, queryActive, queryRole, queryAccountUserType]);

  const onRefresh: Function = useCallback((): void => {
    const filterValues: Record<string, FilterValue> = applyUrlFilters();

    dispatch(getSettingsData());
    dispatch(getUserListData(getOptions(page, limit, filterValues, sortParams)));
  }, [page, limit, sortParams]);

  const onFormChange: Function = useCallback((event: React.SyntheticEvent): void => {
    const fieldName: string = event.target && (event.target as HTMLInputElement).name;

    if (withServerErrors && fieldName) {
      dispatch(resetFormErrors());
      FormService.resetFormServerErrors(formProps, fieldName);
      submitForm();
    }
  }, [withServerErrors, formProps]);

  useEffect((): void => {
    if (isDataLoaded) {
      dispatch(getUserListData(getOptions(page, limit, filters, sortParams)));
    }
  }, [page, limit, sortParams, queryTab]);

  useEffect((): void => {
    if (!isDataLoaded) {
      const filterValues: Record<string, FilterValue> = applyUrlFilters();

      dispatch(getSettingsData());
      dispatch(getUserListData(getOptions(page, limit, filterValues, sortParams)));
    }
  }, [isDataLoaded, queryActive, queryTab]);

  useServerFormErrors(serverFormErrors, (): void => {
    submitForm();
  });

  return (
    <CheckAccess permissions={settingsUserListViewPermissions}>
      <Form id={FORM_ID_SETTINGS_USER_LIST} onChange={onFormChange}>
        <PullToRefresh onRefresh={onRefresh}>
          <ContentHeader title={pageTitle} />
          <SettingsPageTabs
            isCompressed={isRightDrawerOpen}
            activeTab={TAB_NAME_USERS}
            currentUser={currentUser}
          />
          {
            !isDataLoaded && (
              <Loader isPageLoader withTopOffset />
            )
          }
          {
            isDataLoaded && (
              <Fragment>
                <Controls
                  withNoEditMode
                  withFilters
                  withAllFiltersEmpty={withAllFiltersEmpty}
                  createButtonTitle={createButtonTitle}
                  createPermission={SETTINGS_USER_CREATE}
                  filters={filters}
                  currentFilters={currentFilters}
                  onApplyFilters={onApplyFilters}
                  onResetFilters={onResetFilters}
                  onCreateButtonClick={onCreateButtonClick}
                  filterComponent={(
                    <SettingsUserListFilters
                      filters={filters}
                      currentFilters={currentFilters}
                      filterAccountUserTypeList={filteredAccountUserTypeList}
                      filterRoleList={roleList}
                      filterContactCompanyList={contactCompanyList}
                      onSetFilterValue={setFilterValue}
                    />
                  )}
                />
                <FilterPanel
                  filters={filters}
                  filterOptionMap={FILTER_OPTION_MAP}
                  filterKeyMap={FILTER_KEY_MAP}
                  filterLabelMap={FILTER_LABEL_MAP}
                  filterLabelKeyMap={FILTER_LABEL_KEY_MAP}
                  onDeleteFilterValue={onDeleteFilterValue}
                />
                {
                  userListData && !!userListData.length ? (
                    <Fragment>
                      <Card>
                        {
                          !isSmallScreen ? (
                            <ContentListContainerOffset withTabs withPagination withFilters={withFilters}>
                              <Table withStickyHeader>
                                <TableHead isHeader withFixedCellsSize>
                                  <TableRow>
                                    {
                                      tableUserListFields.map((tableField: TableField): ReactElement => (
                                        <TableCell
                                          isSticky={tableField.isSticky}
                                          isStickyLeft={tableField.isSticky && !tableField.isStickyRight}
                                          isStickyLeftHeader={tableField.isSticky && !tableField.isStickyRight}
                                          isStickyRight={tableField.isSticky && tableField.isStickyRight}
                                          isStickyRightHeader={tableField.isSticky && tableField.isStickyRight}
                                          withNoFixedSize={tableField.name === HEADER_NAME_PHOTO}
                                          key={tableField.id}
                                          sortDirection={sortParams.field === tableField.name && tableField.isSortable
                                            ? sortParams.type : false}
                                        >
                                          {
                                            tableField.isSortable ? (
                                              <TableSortLabel
                                                isActive={sortParams.field === tableField.name}
                                                withNoLabel={tableField.isHeaderNameHidden}
                                                sortType={sortParams.type}
                                                onClick={(): void => {
                                                  sortTableRows(tableField.name);
                                                }}
                                              >
                                                {tableField.title}
                                              </TableSortLabel>
                                            ) : (
                                              !tableField.isHeaderNameHidden && (
                                                <TextBlock>
                                                  {tableField.title}
                                                </TextBlock>
                                              )
                                            )
                                          }
                                        </TableCell>
                                      ))
                                    }
                                  </TableRow>
                                </TableHead>
                                <TableBody isPlain withZebraRows withFixedCellsSize withBorderedCells>
                                  {
                                    userListData.map((user: User): ReactElement => (
                                      <TableRow
                                        isButton
                                        key={user.id}
                                        onClick={(event: React.MouseEvent<Element, MouseEvent>): void => {
                                          onRowClick(event, user);
                                        }}
                                      >
                                        {
                                          tableUserListFields.map((tableField: TableField): ReactElement | boolean => (
                                            !!tableField.component && (
                                              <Fragment key={tableField.id}>
                                                {tableField.component(user)}
                                              </Fragment>
                                            )
                                          ))
                                        }
                                      </TableRow>
                                    ))
                                  }
                                </TableBody>
                              </Table>
                            </ContentListContainerOffset>
                          ) : (
                            <ContentListContainerOffset isDefault withTabs withPagination withFilters={withFilters}>
                              <List>
                                {
                                  userListData.map((user: User): ReactElement => (
                                    <ListItem
                                      isButton
                                      key={user.id}
                                      onClick={(event: React.MouseEvent<Element, MouseEvent>): void => {
                                        onRowClick(event, user);
                                      }}
                                    >
                                      {
                                        (!!user.photoThumbnail || !!user.photo) && (
                                          <PaddedFlexContainer>
                                            <Image
                                              isSquare
                                              isListImage
                                              text={user.fullName}
                                              link={user.photoThumbnail || user.photo}
                                            />
                                          </PaddedFlexContainer>
                                        )
                                      }
                                      {
                                        (!user.photoThumbnail && !user.photo) && (
                                          <LetterAvatar wordList={[user.firstName, user.lastName]} />
                                        )
                                      }
                                      <ListItemText isBlock>
                                        {user.fullName}
                                      </ListItemText>
                                    </ListItem>
                                  ))
                                }
                              </List>
                            </ContentListContainerOffset>
                          )
                        }
                      </Card>
                      <TablePagination
                        page={page}
                        limit={limit}
                        totalItems={usersTotal}
                        onPageChange={onPageChange}
                        onLimitChange={onLimitChange}
                      />
                    </Fragment>
                  ) : (
                    <NoContentBlock>
                      No users found by your request
                    </NoContentBlock>
                  )
                }
              </Fragment>
            )
          }
          {
            (!!formError || !!mainServerFormError) && !isFormSaving && (
              <FormError
                withShowError
                withLeftOffset={isRightDrawerOpen}
                error={formError || mainServerFormError}
              />
            )
          }
        </PullToRefresh>
        <SettingsUserModal
          isOpen={isUserModalOpen}
          isSaving={isFormSaving}
          isSaveDisabled={isSaveDisabled}
          isUserLinked={isUserLinked}
          currentUser={currentUser}
          userPhotoFile={userPhotoFiles[FormService.FORM_KEY_NEW]}
          userEmailContactData={userEmailContactData}
          selectedAssetTypes={userAssetTypes[FormService.FORM_KEY_NEW]}
          roleList={roleList}
          assetTypeList={assetTypeList}
          dealList={dealList}
          portfolioList={portfolioList}
          accessLevelList={accessLevelList}
          contactCompanyList={contactCompanyList}
          accountUserTypeList={accountUserTypeList}
          saveFormDeps={saveFormDeps}
          onCreate={onUserCreate}
          onCancel={onCancelForm}
          onCloseModal={closeModal}
          onEmailChange={onEmailChange}
          onLinkContact={onLinkContact}
          onSubmitCreateForm={onUserSubmitCreateForm}
          onAssetTypesChange={onAssetTypesChange}
          onPhoneChange={onPhoneChange}
          onPhoneWorkChange={onPhoneWorkChange}
          onUserPhotoChange={onUserPhotoChange}
          onUserPhotoDelete={onUserPhotoDelete}
          onAddressSearchCompleted={onAddressSearchCompleted}
          onAddressFieldKeyDown={onAddressFieldKeyDown}
          onAccountUserTypeChange={onAccountUserTypeChange}
          onAccountUserDealsChange={onAccountUserDealsChange}
          onAccountUserPortfoliosChange={onAccountUserPortfoliosChange}
          onAccountUserDealAccessLevelChange={onAccountUserDealAccessLevelChange}
          onSendInvitationCheckboxChange={onSendInvitationCheckboxChange}
          onDealSearch={onDealSearch}
          onDealSearchClose={onDealSearchClose}
          onContactCompanySearch={onContactCompanySearch}
          onContactCompanySearchClose={onContactCompanySearchClose}
        />
        <ContextMenu
          menuAnchor={menuAnchor}
          actionMenuItems={actionMenuItems}
          dataAttributeEntityId={DATA_USER_ID}
          dataAttributeEntityType={DATA_IS_ACTIVE}
          dataAttributeExtraEntityType={DATA_IS_PASSWORD_CHANGE_REQUIRED}
          onClose={closeActionMenu}
          onCheckIsMenuItemVisible={onCheckIsMenuItemVisible}
        />
        <SettingsUserInvitationModal
          isOpen={isUserInvitationModalOpen}
          isRightDrawerOpen={isRightDrawerOpen}
          userId={userActiveId}
          userList={userListData}
          onCloseModal={closeUserInvitationModal}
        />
        <ConfirmModal
          isOpen={isDeleteConfirmModalOpen}
          title={deleteConfirmModalTitle}
          text={deleteConfirmModalText}
          errorMessage={confirmModalErrorMessage}
          onConfirm={onDeleteConfirm}
          onCloseModal={closeUserDeleteConfirmModal}
        />
        <ConfirmModal
          isOpen={isDeactivateUserConfirmModalOpen}
          title={deactivateUserConfirmModalTitle}
          text={deactivateUserConfirmModalText}
          errorMessage={confirmModalErrorMessage}
          onConfirm={onDeactivateUserConfirm}
          onCloseModal={closeUserDeactivateConfirmModal}
        />
        <ConfirmModal
          isOpen={isResetPasswordConfirmModalOpen}
          title={resetPasswordConfirmModalTitle}
          text={resetPasswordConfirmModalText}
          errorMessage={confirmModalErrorMessage}
          onConfirm={onResetPasswordUserConfirm}
          onCloseModal={closeUserResetPasswordConfirmModal}
        />
        <StatusBar isOpen={isOpenMessage} message={statusMessage} onClose={closeMessage} />
      </Form>
    </CheckAccess>
  );
});

const initialValues: FormValues = {};
const mapState: ReduxMapStateCallback = (reducers: Reducers): ReduxMapState => {
  const {
    app,
    rightDrawer,
    settingsUserList,
    settings,
  } = reducers;

  const formKeyNew: string = FormService.getIndexedFormFieldKey(FormService.FORM_KEY_NEW);

  initialValues.userListData = {
    [formKeyNew]: {
      id: FormService.FORM_KEY_NEW,
      firstName: null,
      lastName: null,
      email: null,
      role: null,
      accountUserDeals: [],
      accountUserPortfolios: [],
      accountUserType: null,
    },
  };

  return {
    isDataLoaded: !!settings.settingsData && !!settingsUserList.userListData,
    isRightDrawerOpen: rightDrawer.isDrawerOpen,
    currentUser: app.baseData && app.baseData.currentUser,
    userEmailContactData: settingsUserList.userEmailContactData,
    userListData: settingsUserList.userListData && settingsUserList.userListData.userList,
    usersTotal: settingsUserList.userListData && settingsUserList.userListData.usersTotal,
    dealList: settings.settingsData && settings.settingsData.dealList,
    portfolioList: settings.settingsData && settings.settingsData.portfolioList,
    accessLevelList: settings.settingsData && settings.settingsData.accessLevelList,
    contactCompanyList: settings.settingsData && settings.settingsData.contactCompanyList,
    roleList: settings.settingsData && settings.settingsData.roleList,
    assetTypeList: settings.settingsData && settings.settingsData.assetTypeList,
    accountUserTypeList: settings.settingsData && settings.settingsData.accountUserTypeList,
  };
};

const SettingsUserListForm: ReactComponent = (props: ReactComponentProps): ReactElement => {
  const formErrors: ValidationErrors = useSelector(({ settingsUserList }: Reducers): ValidationErrorsMap => (
    settingsUserList.formErrors
  ));
  const isFormSaving: ValidationErrors = useSelector(({ settingsUserList }: Reducers): ValidationErrorsMap => (
    settingsUserList.isFormSaving
  ));

  return (
    <FormContainer
      formId={FORM_ID_SETTINGS_USER_LIST}
      component={SettingsUserList}
      validator={SettingsUserListValidator}
      initialValues={initialValues}
      formErrors={formErrors}
      isSubmitting={isFormSaving}
      {...props}
    />
  );
};

export default connect(mapState)(SettingsUserListForm);
